import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PurchaseShell } from "../features/purchase/PurchaseShell";
import { Toolbar } from "../components/data/Toolbar";
import { FilterRow } from "../components/data/FilterRow";
import { DataTable } from "../components/data/DataTable";
import { NeuButton } from "../components/neu/Neu";
import { useBranchScope } from "../features/purchase/branchScope";
import { ItemImport } from "../features/purchase/ItemImport";
import { ItemStats } from "../features/purchase/ItemStats";
import { itemColumns } from "../features/purchase/itemColumns";
import { backfillReferencePrices, deleteItem, itemStats, listItems } from "../api/masterItems";
import { branchCategories } from "../api/gl";
import { localName } from "../lib/localName";
import { useCachedResource } from "../hooks/useCachedResource";

const TYPES = ["inventory", "consumable", "expense", "asset"];

export default function PurchaseItemsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { branchId } = useBranchScope();
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    const id = setTimeout(() => setQ(search), 250);
    return () => clearTimeout(id);
  }, [search]);

  const { data, reload: load } = useCachedResource(
    `purch-items-${branchId}-${q}-${type}-${category}`,
    async () => ({
      items: (await listItems({ branch_id: branchId || undefined, search: q || undefined,
        item_type: type || undefined, accounting_category: category || undefined, limit: 500 })
        .catch(() => ({ items: [] }))).items || [],
      stats: await itemStats(branchId || undefined).catch(() => null),
    }),
    [branchId, q, type, category],
  );
  const items = data?.items || [];
  const stats = data?.stats || null;

  useEffect(() => {
    if (!branchId) { setCategories([]); return; }
    branchCategories(branchId, type || undefined).then((data) => setCategories(data.items || []))
      .catch(() => setCategories([]));
  }, [type, branchId]);

  const backfill = async () => {
    try { const res = await backfillReferencePrices();
      toast.success(t("purchases.items.backfilled", { count: res.updated })); load();
    } catch { toast.error(t("errors.generic")); }
  };

  const columns = useMemo(() => itemColumns({
    t, i18n, onEdit: (row) => navigate(`/purchases/items/${row.item_id}`),
    onDelete: async (row) => { await deleteItem(row.item_id); toast.success(t("purchases.items.archived")); load(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, i18n, navigate]);

  return (
    <PurchaseShell titleKey="items">
      <Toolbar testid="item-toolbar" search={search} onSearch={setSearch}
               placeholder={t("purchases.items.searchPlaceholder")}
               actionLabel={t("purchases.items.newItem")} onAction={() => navigate("/purchases/items/new")} />
      <div className="flex flex-wrap gap-3">
        <ItemImport onDone={load} branchId={branchId} />
        <NeuButton className="px-4 py-3 text-xs" data-testid="backfill-prices-btn" onClick={backfill}>
          {t("purchases.items.backfill")}
        </NeuButton>
        <NeuButton className="px-4 py-3 text-xs" data-testid="cost-centers-btn"
                   onClick={() => navigate("/purchases/cost-centers")}>
          {t("purchases.items.costCenters")}
        </NeuButton>
      </div>
      <FilterRow testid="item-filters" filters={[
        { key: "type", value: type, onChange: (v) => { setType(v); setCategory(""); },
          allLabel: t("purchases.items.allTypes"),
          options: TYPES.map((key) => ({ value: key, label: t(`purchases.itemTypes.${key}`) })) },
        { key: "category", value: category, onChange: setCategory, allLabel: t("purchases.items.allCategories"),
          options: categories.map((row) => ({ value: row.key, label: localName(row) })) },
      ]} />
      <ItemStats stats={stats} />
      <DataTable testid="items-table" columns={columns} rows={items} rowKey={(row) => row.item_id}
                 onRowClick={(row) => navigate(`/purchases/items/${row.item_id}`)}
                 empty={!branchId ? t("purchases.items.pickBranchFirst") :
                   (search ? t("purchases.noSearchResults") : t("purchases.items.noItems"))} />
    </PurchaseShell>
  );
}
